import React from 'react'
import { useNavigate } from 'react-router'

import useDidMount from '@divvy-web/hooks.usedidmount'
import { FormattedMessage } from '@divvy-web/i18n'
import { BannerType } from '@divvy-web/skylab.banner'
import TextLink, { TextLinkSizes } from '@divvy-web/skylab.textlink'

import { useTrackCollaboratorEvent } from '@sputnik/hooks/useApplicationCollaborators'
import useGetCreditAppTypes from '@sputnik/hooks/useGetCreditAppTypes'
import StatusBanner from '@sputnik/pages/DashboardPages/CreditApplicationsDataGrid/DashboardDetailsDrawer/ApplicationDetailsTabContent/ActionsNeededSection/StatusBanner'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'

interface CorrectionNeededBannerProps {
  salesforceCreditId: string
}

// this banner will show when the status is CORRECTION_REQUESTED (or whatever the new actions needs correction status is called)
const CorrectionNeededBanner = ({ salesforceCreditId }: CorrectionNeededBannerProps) => {
  const navigate = useNavigate()
  const { isEditable } = useGetCreditAppTypes({ salesforceCreditId })

  const { trackCollaboratorEvent } = useTrackCollaboratorEvent({
    salesforceCreditId,
  })

  useDidMount(() => {
    trackCollaboratorEvent('se_signup__application_dashboard_action_needed_correction_needed__presented')
  })

  const onMakeCorrectionsClick = () => {
    trackCollaboratorEvent('se_signup__application_dashboard_action_needed_correction_needed_link__fired', (role) => ({
      person_type: role,
    }))

    navigate(`/app/${salesforceCreditId}/${PATHNAME_CONSTS.REVIEW_PATH}`)
  }

  return (
    <div className='fs-unmask'>
      <StatusBanner
        bannerType={BannerType.Warning}
        subtext={
          isEditable ? (
            <FormattedMessage
              defaultMessage="Some of the information submitted doesn't match our records and needs to be corrected. <link>Make corrections</link>"
              id='sputnik.CorrectionNeededBanner__dezVS5'
              values={{
                link: (chunks) => (
                  <TextLink
                    size={TextLinkSizes.SMALL}
                    onClick={() => onMakeCorrectionsClick()}
                  >
                    {chunks}
                  </TextLink>
                ),
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage='Requested corrections have been completed, but the application still needs to be re-signed and submitted. Please reach out to your authorized signer to submit your corrections.'
              id='sputnik.CorrectionNeededBanner__i+Cevu'
            />
          )
        }
        title={
          isEditable ? (
            <FormattedMessage
              defaultMessage='Correction needed'
              id='sputnik.CorrectionNeededBanner__1JRfgv'
            />
          ) : (
            <FormattedMessage
              defaultMessage='Corrections pending submission'
              id='sputnik.CorrectionNeededBanner__Xc174w'
            />
          )
        }
      />
    </div>
  )
}

export default CorrectionNeededBanner
